import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import {
  Search as SearchIcon, Microscope, FileText, FolderKanban,
  ArrowRight, Phone, User, Car, Mail, Megaphone, AlertCircle, Clock, X,
} from "lucide-react";
import { PriorityBadge } from "@/components/PriorityBadge";

/**
 * MUKTI 2.0 — Home.
 *
 * This replaces the old analytics-first Dashboard as the landing route.
 * The home experience answers "What do you want to investigate?" first,
 * then shows a short Needs-Your-Attention summary.
 */
const DETECT_LABEL = {
  phone: { label: "Phone number", Icon: Phone },
  alias: { label: "Alias / username", Icon: User },
  vehicle: { label: "Vehicle number", Icon: Car },
  email: { label: "Email", Icon: Mail },
  freetext: { label: "Keyword search", Icon: SearchIcon },
  empty: { label: "", Icon: SearchIcon },
};

export default function MuktiHome() {
  const { user } = useAuth();
  const { t } = useLocale();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [attention, setAttention] = useState(null);
  const [sinceLast, setSinceLast] = useState(null);
  const [sinceDismissed, setSinceDismissed] = useState(false);
  const [myCases, setMyCases] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [detected, setDetected] = useState("empty");
  const inputRef = useRef(null);

  const loadAttention = useCallback(async () => {
    try {
      const [c, cases, since] = await Promise.all([
        api.get("/me/changes"),
        api.get("/cases?limit=4"),
        api.get("/me/since-last-visit"),
      ]);
      setAttention(c.data);
      setMyCases((cases.data || []).slice(0, 4));
      setSinceLast(since.data);
    } catch { /* home still renders without these */ }
  }, []);

  const dismissSinceBanner = async () => {
    setSinceDismissed(true);
    try { await api.post("/me/mark-seen"); } catch { /* best-effort */ }
  };

  useEffect(() => { loadAttention(); }, [loadAttention]);

  // Light-weight local detection just for the pill under the search box.
  useEffect(() => {
    const q = query.trim();
    if (!q) { setDetected("empty"); return; }
    const digits = q.replace(/\D/g, "");
    if (digits.length >= 8 && /^\+?\d[\d\s\-]{7,}$/.test(q)) setDetected("phone");
    else if (/^[A-Z]{2}\d{1,2}\s?[A-Z]{1,2}\s?\d{1,4}$/i.test(q)) setDetected("vehicle");
    else if (q.includes("@") && !q.includes(" ")) setDetected("email");
    else if (q.length <= 40 && !q.includes(" ")) setDetected("alias");
    else setDetected("freetext");
  }, [query]);

  const doSearch = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/search/universal?q=${encodeURIComponent(query.trim())}`);
      setSearchResult(data);
    } catch (err) {
      setSearchResult({ error: err?.response?.data?.detail || "Search failed" });
    } finally {
      setSearching(false);
    }
  };

  const detectSpec = DETECT_LABEL[detected] || DETECT_LABEL.empty;
  const DetectIcon = detectSpec.Icon;

  return (
    <div data-testid="mukti-home-page" className="max-w-4xl mx-auto space-y-10 py-4">
      {/* Since you were last here */}
      {sinceLast && !sinceDismissed ? (
        <SinceLastVisitBanner data={sinceLast} onDismiss={dismissSinceBanner} />
      ) : null}

      {/* Hero */}
      <section>
        <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2">
          MUKTI · intelligence network
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
          {t("home.question")}
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">
          {t("home.subtitle")}
        </p>

        <form onSubmit={doSearch} className="mt-5">
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. 9845011220 · @royal_arrangements · Indiranagar · KA01AB1234"
              data-testid="universal-search-input"
              className="h-14 md:h-16 pl-12 pr-40 text-base rounded-xl border-slate-300 focus-visible:ring-slate-900 bg-white shadow-sm"
            />
            {detected !== "empty" ? (
              <span
                data-testid="universal-search-detected"
                className="hidden md:inline-flex items-center gap-1 absolute right-28 top-1/2 -translate-y-1/2 text-[11px] px-2 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200 font-mono"
              >
                <DetectIcon className="h-3 w-3" /> {detectSpec.label}
              </span>
            ) : null}
            <Button
              type="submit"
              disabled={!query.trim() || searching}
              data-testid="universal-search-submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-slate-900 hover:bg-slate-800 h-10"
            >
              {searching ? t("home.searching") : t("home.searchBtn")}
            </Button>
          </div>
        </form>

        {/* Needs your attention — compact row directly below search */}
        {attention ? (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="needs-attention">
            <AttentionCounter
              label="New high-attention leads"
              value={attention.new_high_attention_leads}
              to="/leads?priority=high_attention"
              tone="rose"
              testid="attention-high"
            />
            <AttentionCounter
              label="Watched leads changed"
              value={attention.watched_leads_changed}
              to="/leads"
              tone="amber"
              testid="attention-watched"
            />
            <AttentionCounter
              label="My open cases"
              value={attention.my_open_cases}
              to="/cases"
              tone="slate"
              testid="attention-cases"
            />
          </div>
        ) : null}
      </section>

      {/* Universal search result */}
      {searchResult ? (
        <SearchResultBlock result={searchResult} />
      ) : null}

      {/* My cases strip */}
      {myCases.length ? (
        <section data-testid="my-cases-strip">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-sm uppercase tracking-wider text-slate-500">
              My recent cases
            </h2>
            <Link to="/cases" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              See all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {myCases.map((c) => (
              <Link
                key={c.id}
                to={`/cases/${c.id}`}
                className="border border-slate-200 bg-white rounded-md p-3 hover:border-slate-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-2 text-slate-400 text-[11px] font-mono">
                  <FolderKanban className="h-3 w-3" /> {c.code || c.id?.slice(0, 8)}
                </div>
                <div className="font-medium text-sm text-slate-900 mt-1 line-clamp-2">{c.title}</div>
                <div className="text-[11px] text-slate-500 mt-1">{c.status || "Open"}</div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* Secondary links */}
      <footer className="pt-6 border-t border-slate-100 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Link to="/leads" className="hover:text-slate-900 inline-flex items-center gap-1" data-testid="go-lead-room">
          <Microscope className="h-3 w-3" /> Lead Room
        </Link>
        <span>·</span>
        <Link to="/dashboard" className="hover:text-slate-900 inline-flex items-center gap-1">
          <FileText className="h-3 w-3" /> Legacy analytics dashboard
        </Link>
      </footer>
    </div>
  );
}

function AttentionCounter({ label, value, to, tone, testid }) {
  const toneCls = {
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    slate: "border-slate-200 bg-slate-50 text-slate-900",
  }[tone];
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`border rounded-md px-3 py-2 flex items-center justify-between hover:shadow-sm transition-all ${toneCls}`}
    >
      <span className="text-xs">{label}</span>
      <span className="font-display text-xl font-semibold tabular-nums">{value ?? "—"}</span>
    </Link>
  );
}

function SearchResultBlock({ result }) {
  if (result.error) {
    return (
      <div data-testid="search-error" className="border border-rose-200 bg-rose-50 text-rose-800 rounded-md px-4 py-3 text-sm">
        <AlertCircle className="inline h-4 w-4 mr-1" /> {result.error}
      </div>
    );
  }
  const k = result.knows || {};
  return (
    <section data-testid="search-result-block" className="border border-slate-200 bg-white rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-display font-semibold text-sm uppercase tracking-wider text-slate-700">
          What MUKTI knows about <span className="text-slate-900 font-mono">{result.query}</span>
        </h2>
        <span className="text-[11px] font-mono text-slate-400">
          Detected: <span className="text-slate-700">{result.detected_type}</span>
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 divide-x divide-slate-100 border-b border-slate-100" data-testid="search-knows">
        <KnowsCell label="Ads" value={k.ads} />
        <KnowsCell label="Aliases" value={k.aliases} />
        <KnowsCell label="Images" value={k.images} />
        <KnowsCell label="Locations" value={k.locations} />
        <KnowsCell label="Phones" value={k.phones} />
        <KnowsCell label="Publications" value={k.publications} />
      </div>

      {/* Stands out */}
      <div className="px-5 py-4 border-b border-slate-100" data-testid="search-stands-out">
        <h3 className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">What stands out</h3>
        {result.stands_out?.length ? (
          <ul className="space-y-1.5">
            {result.stands_out.map((s) => (
              <li key={s} className="text-sm text-slate-700 flex gap-2">
                <span className="text-slate-300">•</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500 italic">Nothing unusual yet. Review the records below.</p>
        )}
      </div>

      {/* Existing intelligence */}
      <div className="px-5 py-4 border-b border-slate-100" data-testid="search-existing-leads">
        <h3 className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">
          Existing intelligence
        </h3>
        {result.existing_intelligence?.length ? (
          <div className="space-y-2">
            {result.existing_intelligence.map((L) => (
              <Link
                key={L.id}
                to={`/leads/${L.id}`}
                className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2 hover:bg-slate-50 text-sm"
              >
                <PriorityBadge priority={L.priority} />
                <span className="flex-1 truncate text-slate-900">{L.title}</span>
                <ArrowRight className="h-3 w-3 text-slate-400" />
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">This identifier is not in any lead yet.</p>
        )}
      </div>

      {/* Primary CTA */}
      <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          Investigate pulls everything MUKTI has — records, connections, images, timeline.
        </p>
        <Link
          to={`/investigate?q=${encodeURIComponent(result.query)}`}
          data-testid="search-investigate-btn"
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-4 py-2 rounded-md"
        >
          <Microscope className="h-4 w-4" />
          Investigate connections
        </Link>
      </div>
    </section>
  );
}

function KnowsCell({ label, value }) {
  return (
    <div className="px-3 py-3 text-center">
      <div className="font-display text-2xl font-semibold text-slate-900 tabular-nums">{value ?? 0}</div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}

function SinceLastVisitBanner({ data, onDismiss }) {
  const nl = data?.new_leads?.count || 0;
  const nlh = data?.new_leads?.high_attention_count || 0;
  const wl = data?.watched_leads?.count || 0;
  const cu = data?.cases_updated?.count || 0;
  const total = nl + wl + cu;
  if (!total) return null;  // nothing to say — skip the banner silently
  const since = (data.since || "").slice(0, 10);
  return (
    <section
      data-testid="since-last-banner"
      className="relative rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white px-5 py-4 shadow-sm"
    >
      <button
        type="button"
        onClick={onDismiss}
        data-testid="since-last-dismiss"
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0 mt-0.5">
          <Clock className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-sm text-slate-900">
            Since you were last here <span className="text-slate-400 font-mono text-xs ml-1">({since})</span>
          </h3>
          <ul className="mt-1 text-sm text-slate-700 space-y-0.5">
            {nl ? (
              <li data-testid="since-new-leads">
                <strong className="font-mono">{nl}</strong> new lead(s){nlh ? <> — <strong className="text-rose-700">{nlh} high attention</strong></> : null}.
                {" "}
                <Link to="/leads" className="text-slate-900 underline underline-offset-2 hover:text-slate-700">Review the Lead Room →</Link>
              </li>
            ) : null}
            {wl ? (
              <li data-testid="since-watched-changed">
                <strong className="font-mono">{wl}</strong> lead(s) you are watching changed.
              </li>
            ) : null}
            {cu ? (
              <li data-testid="since-cases-updated">
                <strong className="font-mono">{cu}</strong> case(s) have new activity.
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </section>
  );
}
